import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/api';

const STATUS = {
  inactive: { label: 'Cerrada', color: '#6f7180', className: 'is-inactive' },
  open: { label: 'Abierta', color: '#ffd166', className: 'is-open' },
  needs_attention: { label: 'Pendiente', color: '#ff2d95', className: 'is-alert' },
  closing: { label: 'Cierre', color: '#00f0ff', className: 'is-closing' }
};

const DEFAULT_LAYOUTS = [
  { x: 8, y: 12, w: 13, h: 12, shape: 'round', zone: 'entrada' },
  { x: 28, y: 12, w: 13, h: 12, shape: 'round', zone: 'entrada' },
  { x: 48, y: 12, w: 13, h: 12, shape: 'round', zone: 'entrada' },
  { x: 68, y: 12, w: 13, h: 12, shape: 'round', zone: 'entrada' },
  { x: 12, y: 42, w: 16, h: 12, shape: 'rect', zone: 'sala' },
  { x: 36, y: 42, w: 16, h: 12, shape: 'rect', zone: 'sala' },
  { x: 60, y: 42, w: 16, h: 12, shape: 'rect', zone: 'sala' },
  { x: 16, y: 72, w: 13, h: 12, shape: 'round', zone: 'fondo' },
  { x: 42, y: 72, w: 13, h: 12, shape: 'round', zone: 'fondo' },
  { x: 68, y: 72, w: 13, h: 12, shape: 'round', zone: 'fondo' }
];

function layoutFor(table, index) {
  return table.layout?.x != null ? table.layout : DEFAULT_LAYOUTS[index] || {
    x: 10 + (index % 4) * 22,
    y: 15 + Math.floor(index / 4) * 25,
    w: 14,
    h: 12,
    shape: 'round',
    zone: 'sala'
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function roundPercent(value) {
  return Math.round(value * 10) / 10;
}

function visualStatus(table) {
  if (!table.active) return STATUS.inactive;
  if (table.stats?.pendingOrders > 0) return STATUS.needs_attention;
  if (table.stats?.activeOrders > 0) return { label: 'En servicio', color: '#00ff88', className: 'is-served' };
  return STATUS.open;
}

export default function AdminTables() {
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const planRef = useRef(null);
  const dragRef = useRef(null);
  const movedTableRef = useRef(null);

  const load = () => api.get('/tables').then(data => {
    setTables(data || []);
    setSelected(prev => prev ? (data || []).find(t => t._id === prev._id) || prev : null);
  }).catch(() => {});

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  const summary = useMemo(() => {
    return tables.reduce((acc, table) => {
      if (table.active) acc.open += 1;
      else acc.closed += 1;
      acc.pending += table.stats?.pendingOrders || 0;
      acc.total += table.stats?.total || 0;
      return acc;
    }, { open: 0, closed: 0, pending: 0, total: 0 });
  }, [tables]);

  const nextTableNumber = useMemo(() => {
    return tables.reduce((max, table) => Math.max(max, Number(table.number || 0)), 0) + 1;
  }, [tables]);

  const nextLayout = useMemo(() => {
    const index = tables.length;
    return DEFAULT_LAYOUTS[index] || {
      x: 10 + (index % 4) * 22,
      y: 15 + Math.floor(index / 4) * 18,
      w: 14,
      h: 12,
      shape: 'round',
      zone: 'sala'
    };
  }, [tables.length]);

  const openTable = async (id) => { setError(''); await api.post(`/tables/${id}/open`); load(); };
  const closeTable = async (id) => { setError(''); await api.post(`/tables/${id}/close`); load(); };
  const deleteTable = async (table) => {
    if (!table) return;
    if (table.active || table.currentSessionId) {
      setError('Cierra la mesa antes de eliminarla.');
      return;
    }
    const ok = window.confirm(`¿Eliminar Mesa ${table.number}? También se borrará su QR.`);
    if (!ok) return;
    try {
      setError('');
      await api.del(`/tables/${table._id}`);
      setSelected(null);
      load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la mesa');
    }
  };

  useEffect(() => {
    const handleMove = (event) => {
      const drag = dragRef.current;
      const plan = planRef.current;
      if (!drag || !plan) return;

      const distance = Math.abs(event.clientX - drag.startClientX) + Math.abs(event.clientY - drag.startClientY);
      if (!drag.moved && distance < 4) return;
      drag.moved = true;
      event.preventDefault();

      const rect = plan.getBoundingClientRect();
      const pointerX = ((event.clientX - rect.left) / rect.width) * 100;
      const pointerY = ((event.clientY - rect.top) / rect.height) * 100;
      const nextLayout = {
        ...drag.layout,
        x: roundPercent(clamp(pointerX - drag.offsetX, 0, 100 - drag.layout.w)),
        y: roundPercent(clamp(pointerY - drag.offsetY, 0, 100 - drag.layout.h))
      };

      drag.layout = nextLayout;
      setTables(prev => prev.map(table => table._id === drag.id ? { ...table, layout: nextLayout } : table));
      setSelected(prev => prev?._id === drag.id ? { ...prev, layout: nextLayout } : prev);
    };

    const handleUp = () => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      setDraggingId(null);

      if (!drag.moved) return;
      movedTableRef.current = drag.id;
      window.setTimeout(() => {
        if (movedTableRef.current === drag.id) movedTableRef.current = null;
      }, 80);

      api.patch(`/tables/${drag.id}`, {
        label: drag.table.label || `Mesa ${drag.table.number}`,
        capacity: drag.table.capacity || 4,
        layout: drag.layout
      }).catch(err => {
        setError(err.message || 'No se pudo guardar la posicion de la mesa');
        load();
      });
    };

    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, []);

  const startTableDrag = (event, table, index) => {
    if (event.button != null && event.button !== 0) return;
    const plan = planRef.current;
    if (!plan) return;

    const rect = plan.getBoundingClientRect();
    const layout = layoutFor(table, index);
    const pointerX = ((event.clientX - rect.left) / rect.width) * 100;
    const pointerY = ((event.clientY - rect.top) / rect.height) * 100;

    dragRef.current = {
      id: table._id,
      table,
      layout,
      offsetX: pointerX - layout.x,
      offsetY: pointerY - layout.y,
      startClientX: event.clientX,
      startClientY: event.clientY,
      moved: false
    };
    setDraggingId(table._id);
    setSelected(table);
  };

  const selectTable = (table) => {
    if (movedTableRef.current === table._id) {
      movedTableRef.current = null;
      return;
    }
    setSelected(table);
  };

  const createTable = async (event) => {
    event.preventDefault();
    setError('');
    const form = new FormData(event.currentTarget);
    const payload = {
      number: Number(form.get('number')),
      label: form.get('label') || `Mesa ${form.get('number')}`,
      capacity: Number(form.get('capacity') || 4),
      layout: {
        x: Number(form.get('x')),
        y: Number(form.get('y')),
        w: Number(form.get('w')),
        h: Number(form.get('h')),
        shape: form.get('shape'),
        zone: form.get('zone') || 'sala',
        rotation: Number(form.get('rotation') || 0)
      }
    };
    try {
      const created = await api.post('/tables', payload);
      setCreateOpen(false);
      setSelected(created);
      load();
    } catch (err) {
      setError(err.message || 'No se pudo crear la mesa');
    }
  };

  const updateSelected = async (event) => {
    event.preventDefault();
    if (!selected) return;
    setError('');
    const form = new FormData(event.currentTarget);
    const payload = {
      label: form.get('label') || `Mesa ${selected.number}`,
      capacity: Number(form.get('capacity') || 4),
      layout: {
        x: Number(form.get('x')),
        y: Number(form.get('y')),
        w: Number(form.get('w')),
        h: Number(form.get('h')),
        shape: form.get('shape'),
        zone: form.get('zone') || 'sala',
        rotation: Number(form.get('rotation') || 0)
      }
    };
    try {
      const updated = await api.patch(`/tables/${selected._id}`, payload);
      setSelected(prev => ({ ...prev, ...updated }));
      load();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la mesa');
    }
  };

  const printQr = (table) => {
    const qrUrl = table.qrUrl;
    const publicUrl = table.publicFullUrl || `${window.location.origin}${table.publicUrl}`;
    const html = `<!doctype html>
      <html><head><title>QR Mesa ${table.number}</title>
      <style>
        body{font-family:Arial,sans-serif;background:#fff;color:#0a0a12;margin:0;padding:32px;text-align:center}
        .card{border:2px solid #111;border-radius:18px;padding:28px;max-width:420px;margin:0 auto}
        h1{margin:0 0 4px;font-size:28px} h2{margin:0 0 20px;font-size:22px}
        img{width:300px;height:300px;object-fit:contain} p{font-size:16px;margin:14px 0}.url{font-size:12px;word-break:break-all}
      </style></head><body>
      <div class="card"><h1>NEON Music Cocktail</h1><h2>Mesa ${table.number}</h2>
      <img src="${qrUrl}" alt="QR Mesa ${table.number}" />
      <p>Escanea para ver la carta y pedir desde tu mesa</p>
      <p class="url">${publicUrl}</p></div>
      <script>window.onload=()=>window.print()</script></body></html>`;
    const w = window.open('', '_blank', 'width=520,height=720');
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="neon-admin-page">
      <div className="neon-admin-heading">
        <div>
          <h2>Control de mesas</h2>
          <p>Plano de sala, QR y sesiones abiertas. Arrastra una mesa para recolocarla.</p>
        </div>
        <div className="neon-admin-heading-actions">
          <button className="neon-btn neon-btn-primary" type="button" onClick={() => setCreateOpen(true)}>Crear mesa</button>
          <span className="neon-poll">auto-refresh 10s</span>
        </div>
      </div>

      {error && <div className="neon-admin-error">{error}</div>}

      <div className="neon-room-summary">
        <div><span>{summary.open}</span><small>Mesas abiertas</small></div>
        <div><span>{summary.closed}</span><small>Cerradas</small></div>
        <div><span>{summary.pending}</span><small>Pedidos pendientes</small></div>
        <div><span>{summary.total.toFixed(2)} €</span><small>Total abierto</small></div>
      </div>

      <div className="neon-room-workspace">
        <section ref={planRef} className="neon-room-plan" aria-label="Plano rectangular de sala">
          <div className="neon-room-zone neon-room-zone-bar">Barra / entrada</div>
          <div className="neon-room-zone neon-room-zone-floor">Sala</div>
          <div className="neon-room-zone neon-room-zone-back">Fondo</div>
          {tables.map((table, index) => {
            const layout = layoutFor(table, index);
            const state = visualStatus(table);
            return (
              <button
                key={table._id}
                type="button"
                className={`neon-room-table ${layout.shape === 'rect' ? 'is-rect' : ''} ${state.className} ${selected?._id === table._id ? 'is-selected' : ''} ${draggingId === table._id ? 'is-dragging' : ''}`}
                style={{
                  left: `${layout.x}%`,
                  top: `${layout.y}%`,
                  width: `${layout.w}%`,
                  height: `${layout.h}%`,
                  transform: `rotate(${layout.rotation || 0}deg)`,
                  borderColor: state.color
                }}
                onPointerDown={(event) => startTableDrag(event, table, index)}
                onClick={() => selectTable(table)}
              >
                <strong>M{table.number}</strong>
                <span>{state.label}</span>
                {table.stats?.pendingOrders > 0 && <em>{table.stats.pendingOrders}</em>}
              </button>
            );
          })}
        </section>

        <aside className="neon-table-detail">
          {selected ? (
            <>
              <div className="neon-table-detail-head">
                <div>
                  <h3>Mesa {selected.number}</h3>
                  <p>{selected.label || 'Sin etiqueta'} · Capacidad {selected.capacity}</p>
                </div>
                <span style={{ color: visualStatus(selected).color }}>{visualStatus(selected).label}</span>
              </div>

              <div className="neon-table-detail-total">
                <span>Total sesión</span>
                <strong>{(selected.stats?.total || 0).toFixed(2)} €</strong>
              </div>

              <div className="neon-table-detail-actions">
                {!selected.active && <button className="neon-btn neon-btn-primary" onClick={() => openTable(selected._id)}>Abrir mesa</button>}
                {selected.active && <button className="neon-btn neon-btn-secondary" onClick={() => closeTable(selected._id)}>Cerrar mesa</button>}
                <button className="neon-btn neon-btn-ghost" onClick={() => setQrModal(selected)}>Ver QR</button>
                {!selected.active && <button className="neon-btn neon-btn-danger" onClick={() => deleteTable(selected)}>Eliminar mesa</button>}
              </div>

              <div className="neon-table-qr-card">
                <img src={selected.qrUrl} alt={`QR Mesa ${selected.number}`} />
                <div>
                  <p>URL pública</p>
                  <a href={selected.publicUrl} target="_blank" rel="noreferrer">{selected.publicFullUrl || selected.publicUrl}</a>
                </div>
              </div>

              <div className="neon-table-detail-actions">
                <a className="neon-btn neon-btn-ghost" href={selected.qrUrl} download={`mesa-${String(selected.number).padStart(2, '0')}.png`}>Descargar QR</a>
                <button className="neon-btn neon-btn-ghost" onClick={() => printQr(selected)}>Imprimir ficha</button>
              </div>

              <div className="neon-table-orders-mini">
                <h4>Pedidos de la sesión</h4>
                {selected.currentSessionId?.orders?.length ? (
                  selected.currentSessionId.orders.map(order => (
                    <div key={order._id} className="neon-mini-order">
                      <span>{order.status}</span>
                      <strong>{order.total.toFixed(2)} €</strong>
                    </div>
                  ))
                ) : <p>Sin pedidos activos.</p>}
              </div>

              <form
                key={`${selected._id}-${layoutFor(selected, tables.findIndex(t => t._id === selected._id)).x}-${layoutFor(selected, tables.findIndex(t => t._id === selected._id)).y}`}
                className="neon-table-edit-form"
                onSubmit={updateSelected}
              >
                <h4>Ajustes de mesa</h4>
                <div className="neon-form-grid">
                  <label>Etiqueta<input name="label" defaultValue={selected.label || ''} /></label>
                  <label>Capacidad<input name="capacity" type="number" min="1" defaultValue={selected.capacity || 4} /></label>
                  <label>X %<input name="x" type="number" min="0" max="95" defaultValue={layoutFor(selected, tables.findIndex(t => t._id === selected._id)).x} /></label>
                  <label>Y %<input name="y" type="number" min="0" max="95" defaultValue={layoutFor(selected, tables.findIndex(t => t._id === selected._id)).y} /></label>
                  <label>Ancho %<input name="w" type="number" min="6" max="35" defaultValue={layoutFor(selected, tables.findIndex(t => t._id === selected._id)).w} /></label>
                  <label>Alto %<input name="h" type="number" min="6" max="30" defaultValue={layoutFor(selected, tables.findIndex(t => t._id === selected._id)).h} /></label>
                  <label>Forma
                    <select name="shape" defaultValue={layoutFor(selected, tables.findIndex(t => t._id === selected._id)).shape || 'round'}>
                      <option value="round">Redonda</option>
                      <option value="square">Cuadrada</option>
                      <option value="rect">Rectangular</option>
                    </select>
                  </label>
                  <label>Zona<input name="zone" defaultValue={layoutFor(selected, tables.findIndex(t => t._id === selected._id)).zone || 'sala'} /></label>
                  <label>Rotación<input name="rotation" type="number" min="-45" max="45" defaultValue={layoutFor(selected, tables.findIndex(t => t._id === selected._id)).rotation || 0} /></label>
                </div>
                <button className="neon-btn neon-btn-primary" type="submit">Guardar ajustes</button>
              </form>
            </>
          ) : (
            <div className="neon-table-empty-detail">
              <h3>Selecciona una mesa</h3>
              <p>Desde aquí puedes abrirla, cerrarla, ver su QR y revisar la cuenta.</p>
            </div>
          )}
        </aside>
      </div>

      <section className="neon-qr-list">
        <h3>QR de mesas</h3>
        <div className="neon-qr-grid">
          {tables.map(table => (
            <button key={table._id} type="button" onClick={() => setQrModal(table)}>
              <img src={table.qrUrl} alt={`QR Mesa ${table.number}`} />
              <span>Mesa {table.number}</span>
            </button>
          ))}
        </div>
      </section>

      {qrModal && (
        <div className="neon-modal-backdrop" onClick={() => setQrModal(null)}>
          <div className="neon-qr-modal" onClick={e => e.stopPropagation()}>
            <button className="neon-modal-close" onClick={() => setQrModal(null)}>×</button>
            <h3>QR Mesa {qrModal.number}</h3>
            <img src={qrModal.qrUrl} alt={`QR Mesa ${qrModal.number}`} />
            <a href={qrModal.publicUrl} target="_blank" rel="noreferrer">{qrModal.publicFullUrl || qrModal.publicUrl}</a>
            <div className="neon-table-detail-actions">
              <a className="neon-btn neon-btn-primary" href={qrModal.qrUrl} download={`mesa-${String(qrModal.number).padStart(2, '0')}.png`}>Descargar</a>
              <button className="neon-btn neon-btn-secondary" onClick={() => printQr(qrModal)}>Imprimir</button>
            </div>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="neon-modal-backdrop" onClick={() => setCreateOpen(false)}>
          <form className="neon-table-create-modal" onSubmit={createTable} onClick={e => e.stopPropagation()}>
            <button className="neon-modal-close" type="button" onClick={() => setCreateOpen(false)}>×</button>
            <h3>Crear mesa</h3>
            <p>Se generará automáticamente su QR y quedará cerrada hasta que un camarero la abra.</p>
            <div className="neon-form-grid">
              <label>Número<input name="number" type="number" min="1" defaultValue={nextTableNumber} required /></label>
              <label>Etiqueta<input name="label" defaultValue={`Mesa ${nextTableNumber}`} /></label>
              <label>Capacidad<input name="capacity" type="number" min="1" defaultValue="4" /></label>
              <label>X %<input name="x" type="number" min="0" max="95" defaultValue={nextLayout.x} /></label>
              <label>Y %<input name="y" type="number" min="0" max="95" defaultValue={nextLayout.y} /></label>
              <label>Ancho %<input name="w" type="number" min="6" max="35" defaultValue={nextLayout.w} /></label>
              <label>Alto %<input name="h" type="number" min="6" max="30" defaultValue={nextLayout.h} /></label>
              <label>Forma
                <select name="shape" defaultValue={nextLayout.shape || 'round'}>
                  <option value="round">Redonda</option>
                  <option value="square">Cuadrada</option>
                  <option value="rect">Rectangular</option>
                </select>
              </label>
              <label>Zona<input name="zone" defaultValue={nextLayout.zone || 'sala'} /></label>
              <label>Rotación<input name="rotation" type="number" min="-45" max="45" defaultValue={nextLayout.rotation || 0} /></label>
            </div>
            <button className="neon-btn neon-btn-primary" type="submit">Crear mesa con QR</button>
          </form>
        </div>
      )}
    </div>
  );
}
